import React, { useState, useEffect } from 'react';
import {
  Send,
  Settings,
  Zap,
  FileText,
  AlertCircle,
  Plus,
  Trash2,
  RefreshCw,
  Eye,
  EyeOff,
  TestTube,
  Save,
  Clock,
  CheckCircle,
  XCircle,
  Activity,
  Mail,
  MessageCircle,
  Phone
} from 'lucide-react';
import { toast } from 'react-toastify';

interface Template {
  id: string;
  name: string;
  status: 'APPROVED' | 'PENDING' | 'REJECTED';
  category: string;
  language: string;
  parameters: string[];
  content: string;
  channel: 'whatsapp' | 'sms' | 'email';
}

interface TriggerRule {
  id: string;
  name: string;
  triggerType: 'new_lead' | 'status_change' | 'custom';
  fromStatus?: string;
  toStatus?: string;
  templateId: string;
  channel: 'whatsapp' | 'sms' | 'email';
  parameterMapping: Record<string, string>;
  isActive: boolean;
}

const BrevoSettings: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'config' | 'templates' | 'automation' | 'logs'>('config');
  const [, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [templates] = useState<Template[]>([]);
  const [triggers] = useState<TriggerRule[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const [, setShowTriggerModal] = useState(false);

  // Configuration State
  const [config, setConfig] = useState({
    apiKey: '',
    accountId: '',
    senderName: '',
    emailEnabled: true,
    isActive: false,
    webhookUrl: ''
  });

  // Message Logs State
  const [logs] = useState<any[]>([]);
  const [logsFilter, setLogsFilter] = useState<'all' | 'sent' | 'failed'>('all');

  // Load configuration on mount
  useEffect(() => {
    loadConfiguration();
    if (activeTab === 'templates') {
      loadTemplates();
    }
    if (activeTab === 'automation') {
      loadTriggers();
    }
    if (activeTab === 'logs') {
      loadLogs();
    }
  }, [activeTab]);

  const loadConfiguration = async () => {
    setIsLoading(true);
    try {
      // API call to load config
      // const response = await fetch('/api/integrations/brevo/settings');
      // const data = await response.json();
      // setConfig(data);
    } catch (error) {
      toast.error('Failed to load configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      // API call to load templates
      // const response = await fetch('/api/brevo/templates');
      // const data = await response.json();
      // setTemplates(data);
    } catch (error) {
      toast.error('Failed to load templates');
    }
  };

  const loadTriggers = async () => {
    try {
      // API call to load triggers
      // const response = await fetch('/api/brevo/triggers');
      // const data = await response.json();
      // setTriggers(data);
    } catch (error) {
      toast.error('Failed to load automation rules');
    }
  };

  const loadLogs = async () => {
    try {
      // API call to load logs
      // const response = await fetch('/api/brevo/logs');
      // const data = await response.json();
      // setLogs(data);
    } catch (error) {
      toast.error('Failed to load message logs');
    }
  };

  const handleSaveConfig = async () => {
    setIsSaving(true);
    try {
      // API call to save configuration
      toast.success('Configuration saved successfully');
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      // API call to test connection
      toast.success('Connection successful!');
    } catch (error) {
      toast.error('Connection failed. Please check your API key.');
    }
  };

  const handleSyncTemplates = async () => {
    setIsSyncing(true);
    try {
      // API call to sync templates
      toast.success('Templates synchronized successfully');
      await loadTemplates();
    } catch (error) {
      toast.error('Failed to sync templates');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleTestMessage = async (_templateId: string, channel: string) => {
    try {
      // API call to send test message
      toast.success(`Test ${channel} message sent successfully`);
    } catch (error) {
      toast.error(`Failed to send test ${channel} message`);
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'whatsapp':
        return <MessageCircle className="h-4 w-4" />;
      case 'sms':
        return <Phone className="h-4 w-4" />;
      case 'email':
        return <Mail className="h-4 w-4" />;
      default:
        return <Send className="h-4 w-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-lg">
                <Send className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Brevo Integration</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Multi-channel messaging with WhatsApp, SMS & Email
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                {config.emailEnabled && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 rounded-full text-xs font-medium">
                    Email
                  </span>
                )}
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                config.isActive
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
              }`}>
                {config.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6">
          <div className="flex space-x-8 border-b border-gray-200 dark:border-gray-700">
            {[
              { id: 'config', label: 'Configuration', icon: Settings },
              { id: 'templates', label: 'Templates', icon: FileText },
              { id: 'automation', label: 'Automation', icon: Zap },
              { id: 'logs', label: 'Message Logs', icon: Activity }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-2 px-1 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600 dark:border-purple-400 dark:text-purple-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Configuration Tab */}
        {activeTab === 'config' && (
          <div className="max-w-4xl">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    API Configuration
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showApiKey ? 'text' : 'password'}
                          value={config.apiKey}
                          onChange={(e) => setConfig({ ...config, apiKey: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                          placeholder="Enter your Brevo API key"
                        />
                        <button
                          onClick={() => setShowApiKey(!showApiKey)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          {showApiKey ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        You can find your API key in Brevo dashboard under Settings → API Keys
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Account ID
                      </label>
                      <input
                        type="text"
                        value={config.accountId}
                        onChange={(e) => setConfig({ ...config, accountId: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., account_123456"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Sender Name
                      </label>
                      <input
                        type="text"
                        value={config.senderName}
                        onChange={(e) => setConfig({ ...config, senderName: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., Your Company"
                      />
                    </div>

                    {/* Email Configuration Info */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <div className="flex items-center space-x-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                        <Mail className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        <div className="flex-1">
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Email Integration</span>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Send transactional emails and marketing campaigns</p>
                        </div>
                        <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full text-xs font-medium">
                          Enabled
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between py-4 px-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="active"
                          checked={config.isActive}
                          onChange={(e) => setConfig({ ...config, isActive: e.target.checked })}
                          className="h-4 w-4 text-purple-600 rounded focus:ring-purple-500"
                        />
                        <label htmlFor="active" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Enable Integration
                        </label>
                      </div>
                      <button
                        onClick={handleTestConnection}
                        className="flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-colors"
                      >
                        <TestTube className="h-4 w-4" />
                        <span>Test Connection</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleSaveConfig}
                    disabled={isSaving}
                    className="flex items-center space-x-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSaving ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    <span>{isSaving ? 'Saving...' : 'Save Configuration'}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Brevo Features Info */}
            <div className="mt-6 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-purple-700 dark:text-purple-300">
                  <p className="font-medium mb-1">Brevo Multi-Channel Features</p>
                  <ul className="list-disc list-inside space-y-1 text-purple-600 dark:text-purple-400">
                    <li>Unified messaging across WhatsApp, SMS, and Email</li>
                    <li>Advanced analytics and reporting</li>
                    <li>Contact segmentation and targeting</li>
                    <li>Marketing automation workflows</li>
                    <li>Real-time delivery tracking</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div>
            <div className="mb-6 flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Email Templates
                </h3>
                <div className="flex items-center space-x-2">
                  <span className="flex items-center space-x-1 px-3 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 rounded-lg text-sm font-medium">
                    <Mail className="h-4 w-4" />
                    <span>Email</span>
                  </span>
                </div>
              </div>
              <button
                onClick={handleSyncTemplates}
                disabled={isSyncing}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
                <span>{isSyncing ? 'Syncing...' : 'Sync Templates'}</span>
              </button>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                {templates.filter(t => t.channel === 'email').length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">No email templates found</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                      Click "Sync Templates" to fetch templates from Brevo
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {templates.filter(t => t.channel === 'email').map((template) => (
                      <div
                        key={template.id}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h4 className="font-medium text-gray-900 dark:text-white">
                                {template.name}
                              </h4>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                template.status === 'APPROVED'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : template.status === 'PENDING'
                                  ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                                  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              }`}>
                                {template.status}
                              </span>
                              <span className="flex items-center space-x-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full">
                                {getChannelIcon(template.channel)}
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                  {template.channel}
                                </span>
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              Category: {template.category} | Language: {template.language}
                            </p>
                            <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded">
                              <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                                {template.content}
                              </p>
                            </div>
                            {template.parameters.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  Parameters: {template.parameters.join(', ')}
                                </p>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => handleTestMessage(template.id, template.channel)}
                            className="ml-4 px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          >
                            Send Test
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Automation Tab */}
        {activeTab === 'automation' && (
          <div>
            <div className="mb-6 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Automation Rules
              </h3>
              <button
                onClick={() => setShowTriggerModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Add Rule</span>
              </button>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                {triggers.length === 0 ? (
                  <div className="text-center py-12">
                    <Zap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">No automation rules configured</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
                      Create rules to automatically send messages across channels
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {triggers.map((trigger) => (
                      <div
                        key={trigger.id}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3">
                              <h4 className="font-medium text-gray-900 dark:text-white">
                                {trigger.name}
                              </h4>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                trigger.isActive
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                              }`}>
                                {trigger.isActive ? 'Active' : 'Inactive'}
                              </span>
                              <span className="flex items-center space-x-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full">
                                {getChannelIcon(trigger.channel)}
                                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                  {trigger.channel}
                                </span>
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              Trigger: {trigger.triggerType === 'new_lead' ? 'New Lead' :
                                       trigger.triggerType === 'status_change' ? 'Status Change' : 'Custom'}
                              {trigger.triggerType === 'status_change' && (
                                <span> ({trigger.fromStatus} → {trigger.toStatus})</span>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                              <Settings className="h-4 w-4" />
                            </button>
                            <button className="p-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <div>
            <div className="mb-6 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Message Logs
              </h3>
              <div className="flex items-center space-x-2">
                {['all', 'sent', 'failed'].map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setLogsFilter(filter as any)}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      logsFilter === filter
                        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Channel
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Recipient
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Template
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {logs.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center">
                          <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-500 dark:text-gray-400">No messages sent yet</p>
                        </td>
                      </tr>
                    ) : (
                      logs.map((log, index) => (
                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                            {log.time}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="flex items-center space-x-1">
                              {getChannelIcon(log.channel)}
                              <span className="text-sm text-gray-900 dark:text-gray-300 capitalize">
                                {log.channel}
                              </span>
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                            {log.recipient}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300">
                            {log.template}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              log.status === 'sent'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : log.status === 'failed'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                            }`}>
                              {log.status === 'sent' && <CheckCircle className="h-3 w-3 mr-1" />}
                              {log.status === 'failed' && <XCircle className="h-3 w-3 mr-1" />}
                              {log.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                              {log.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {log.details}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BrevoSettings;